import * as React from "react";
import { useLocation } from "react-router-dom";

import { Error500 } from ".";

interface ErrorBoundaryState {
  hasError: boolean;
}

const usePrevious = <T extends unknown>(value: T): T => {
  const ref = React.useRef<T>();

  React.useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

interface LocationErrorResetProps {
  resetError: () => void;
  hasError: boolean;
}

const LocationErrorReset: React.FC<LocationErrorResetProps> = ({
  resetError,
  hasError,
}) => {
  const location = useLocation();
  const prevHasError = usePrevious(hasError);

  React.useEffect(() => {
    if (prevHasError) {
      resetError();
    }
  }, [location, prevHasError, resetError]);

  return null;
};

export class ErrorBoundary extends React.Component<{}, ErrorBoundaryState> {
  constructor(props) {
    super(props);
    this.state = { hasError: false };

    this.resetError = this.resetError.bind(this);
  }

  resetError(): void {
    this.setState({
      hasError: false,
    });
  }

  static getDerivedStateFromError(error): ErrorBoundaryState {
    console.log(error);
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo): void {
    console.log(error);
    // You can also log the error to an error reporting service
    // logErrorToMyService(error, errorInfo);
  }

  render(): React.ReactNode {
    const { children } = this.props;

    return (
      <>
        <LocationErrorReset
          resetError={this.resetError}
          hasError={this.state.hasError}
        />
        {this.state.hasError ? <Error500 /> : children}
      </>
    );
  }
}
